import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Stack, StackItem } from '@patternfly/react-core';
import _ from 'lodash';

import { useTranslation } from 'localization/useTranslation';
import ModelType from 'components/Model/ModelType';
import ModelsContainer from 'components/Model/ModelsContainer';
import SimpleTable, { SortDirection, RowSelection } from 'components/Common/SimpleTable/SimpleTable';
import Tl from 'localization/LocalizationKeys';
import { format } from 'utils/string';
import { bindingDescriptionIdFromBindingContentId, getUniqueContentBindingDescriptionIds } from '../../utils/model';

const columns = [
  {
    label: Tl.common.name,
    path: 'displayName',
    isSortable: true,
    searchItem: true
  },
  {
    label: 'Binding',
    path: 'bindingDescriptions',
    isSortable: true,
    searchItem: true
  }
];

const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};


const SelectAssetsStep = ({ models, assets, isLoadingModels, isLoadingAssets, migrationInfo, setMigrationInfo, setIsValid }) => {
  const T = useTranslation();
  const [hasError] = useState(false);

  // TODO: translate
  const assetsWithBindings = useMemo(() => {
    if (_.isEmpty(assets)) {
      return [];
    }
    const a = _.map(assets, asset => {
      const [_default, rest] = _.partition(asset.contentBindings, binding => _.includes(binding?.contentBindingDescriptionId, '_default'));
      const _def = !_.isEmpty(_default) ? ['Default'] : [];
      const uniq = _.uniq(_.map(rest, r => bindingDescriptionIdFromBindingContentId(r.contentBindingDescriptionId)));
      if (_.isEmpty(_default) && _.isEmpty(rest)) {
        return { ...asset, bindingDescriptions: 'No Source' };
      }
      return { ...asset, bindingDescriptions: _.join(_.compact(_.concat(_def, uniq)), ', ') };
    });
    return a;
  }, [assets]);

  const filteredAssets = useMemo(() => {
    if (!_.isEmpty(migrationInfo?.sourceModel?.id)) {
      const baseModelId = _.first(_.split(migrationInfo?.sourceModel?.id, '.'));
      return _.filter(assetsWithBindings, asset => asset.modelId === baseModelId);
    }
    return [];
  }, [assetsWithBindings, migrationInfo?.sourceModel?.id]);

  const selectedAssets = useMemo(() => {
    const selected = _.filter(filteredAssets, asset => _.includes(migrationInfo?.targetAssetIds, asset.id));
    if (migrationInfo?.targetAssetIds !== undefined && _.size(selected) === _.size(migrationInfo?.targetAssetIds)) {
      return selected;
    }

    return filteredAssets;
  }, [filteredAssets, migrationInfo?.targetAssetIds]);

  useEffect(() => {
    setMigrationInfo(oldInfo => {
      if (oldInfo?.targetAssetIds == null || _.size(selectedAssets) !== _.size(oldInfo?.targetAssetIds)) {
        return {
          ...oldInfo,
          targetAssetIds: _.uniq(_.map(selectedAssets, asset => asset.id))
        };
      }
      return oldInfo;
    }
    );
  }, [selectedAssets, setMigrationInfo]);

  const onSelectedRows = useCallback((assets) => {
    const { assetBindingIds, defaultBindingIds } = getUniqueContentBindingDescriptionIds(assets);
    setMigrationInfo(oldInfo => ({
      ...oldInfo,
      targetAssetIds: _.uniq(_.map(assets, asset => asset.id)),
      currentAssetBindingIds: assetBindingIds,
      currentDefaultBindingIds: defaultBindingIds
    }));
  }, [setMigrationInfo]);

  useEffect(() => {
    setIsValid(!_.isEmpty(migrationInfo?.targetAssetIds));
  }, [migrationInfo?.targetAssetIds, setIsValid]);

  // TODO: translate
  const assetModel = useMemo(() => {
    return [
      {
        type: ModelType.OPTIONSV2,
        label: 'Current Asset Model',
        path: 'sourceModel',
        isRequired: true,
        hasError: _.isEmpty,
        // TODO: remove target model from the list
        options: _.map(models, model => ({ value: model, text: model.displayName, description: model.id })),
        properties: {
          isLoading: isLoadingAssets,
          iteratee: _.iteratee('id'),
          isClearable: true,
          isTypeahead: true
        }
      }
    ];
  }, [isLoadingAssets, models]);

  // TODO: translate
  const globalActions = [
    {
      label: format('Clear Selection ({0})', _.size(migrationInfo.targetAssetIds)),
      onClick: () => {
        setMigrationInfo(oldInfo => ({
          ...oldInfo,
          targetAssetIds: undefined
        }));
      }
    }
  ];

  return (
    <Stack>
      <StackItem >
        <ModelsContainer models={assetModel} data={migrationInfo} setData={setMigrationInfo} />
      </StackItem>
      <StackItem>
        <SimpleTable
          isLoading={_.isEmpty(assets) || isLoadingModels}
          data={filteredAssets}
          columns={columns}
          rowSelection={RowSelection.MULTI_CHECKBOX}
          selectedRows={selectedAssets}
          onSelectedRows={onSelectedRows}
          sortBy={DefaultSortBy}
          hasError={hasError}
          errorText={T.admin.model.list.error}
          compact
          globalActions={globalActions}
        />
      </StackItem>
    </Stack>
  );
};

export default SelectAssetsStep;
